import type { ChangeEventHandler } from 'react';
import { FieldArray } from 'formik';
import InputText from '../../../../components/input-text/inputText';
import Heading from '../../../../components/typography';
import { COLORS } from '../../../../constants/style/color';

interface AllowedRedirectUrlHostsFieldProps {
  values: { allowedReturnUrlHosts: string[] };
  errors: Record<string, unknown>;
  touched: Record<string, unknown>;
  handleChange: ChangeEventHandler<unknown>;
}

const AllowedRedirectUrlHostsField = ({
  values,
  errors,
  touched,
  handleChange,
}: AllowedRedirectUrlHostsFieldProps): JSX.Element => {
  const arrayError =
    typeof errors.allowedReturnUrlHosts === 'string' ? errors.allowedReturnUrlHosts : undefined;
  const arrayTouched = Boolean(touched.allowedReturnUrlHosts);

  return (
    <div id='allowedRedirectUrlHostsField'>
      <Heading variant='body-xs' title='Allowed redirect hosts' className='detailLabel mb-2' />
      <Heading
        variant='body-xs'
        color={COLORS.SpanishGray}
        className='mb-3'
        title='Add host names for callback URLs your application uses after enrollment. Wealthlane only redirects to these hosts when enrollment completes or is cancelled.'
      />
      <FieldArray
        name='allowedReturnUrlHosts'
        render={(arrayHelpers) => (
          <div className='flex flex-col gap-3'>
            {values.allowedReturnUrlHosts.map((_, index) => (
              <div key={`allowed-return-url-${index}`} id={`allowedReturnUrlRow.${index}`}>
                {index === 0 && (
                  <Heading variant='body-xs' title='Redirect host' className='detailLabel mb-1' />
                )}
                <div className='flex w-full items-center gap-2'>
                  <div className='flex-1 min-w-0'>
                    <InputText
                      type='text'
                      name={`allowedReturnUrlHosts.${index}`}
                      id={`allowedReturnUrlHosts.${index}`}
                      placeholder='localhost'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      required={index === 0}
                    />
                  </div>
                  <div
                    className='flex shrink-0 items-center gap-1'
                    id={`allowedReturnUrlActions.${index}`}>
                    {values.allowedReturnUrlHosts.length > 1 && (
                      <button
                        type='button'
                        className='flex items-center justify-center cursor-pointer p-1 rounded hover:bg-gray-100'
                        id={`allowedReturnUrlRemove.${index}`}
                        aria-label='Remove redirect host'
                        onClick={() => {
                          arrayHelpers.remove(index);
                        }}>
                        <span
                          className='material-symbols-outlined text-red-600'
                          style={{ fontSize: '22px' }}>
                          delete
                        </span>
                      </button>
                    )}
                    {index === values.allowedReturnUrlHosts.length - 1 && (
                      <button
                        type='button'
                        className='flex items-center justify-center cursor-pointer p-1 rounded hover:bg-gray-100'
                        id={`allowedReturnUrlAdd.${index}`}
                        aria-label='Add redirect host'
                        onClick={() => {
                          arrayHelpers.push('');
                        }}>
                        <span
                          className='material-symbols-outlined text-green-600'
                          style={{ fontSize: '22px' }}>
                          add_circle
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {arrayError && arrayTouched && (
              <Heading variant='body-xs' color={COLORS.Red} title={arrayError} />
            )}
          </div>
        )}
      />
    </div>
  );
};

export default AllowedRedirectUrlHostsField;
